/**
 * The church office catalogue — one shared map for every role picker.
 *
 * Read from the server (`GET /api/admin/roles/church-offices`, derived from the
 * canonical registry) so labels and tiers can never drift from what the
 * backend enforces. The bare list below is only a bootstrap fallback for an
 * offline first paint; the moment the fetch lands it is replaced.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';

const FALLBACK = ['member', 'elder', 'deacon', 'pastor', 'youth', 'worship',
                  'usher', 'admin', 'visitor'];

const titleise = (key) => (key || '')
  .split('_')
  .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
  .join(' ');

export const FALLBACK_GROUPS = [{
  key: 'general',
  label: 'Church roles',
  roles: FALLBACK.map((key) => ({ key, label: titleise(key), level: 'assembly',
                                  category: 'general', tier: 'viewer',
                                  permissions: [], opens_workspace: false })),
}];

let cache = null;
let inflight = null;

export const loadChurchRoles = async () => {
  if (cache) return cache;
  if (!inflight) {
    inflight = api.get('/admin/roles/church-offices')
      .then((r) => {
        const groups = (r.data?.groups || []).filter((g) => (g.roles || []).length);
        cache = groups.length ? groups : FALLBACK_GROUPS;
        return cache;
      })
      .catch(() => FALLBACK_GROUPS)
      .finally(() => { inflight = null; });
  }
  return inflight;
};

/** Grouped offices + a flat list + a label lookup, for selects and chips. */
export const useChurchRoles = () => {
  const [groups, setGroups] = useState(cache || FALLBACK_GROUPS);

  useEffect(() => {
    let alive = true;
    loadChurchRoles().then((g) => { if (alive) setGroups(g); });
    return () => { alive = false; };
  }, []);

  const roles = groups.flatMap((g) => g.roles || []);
  const labels = Object.fromEntries(roles.map((r) => [r.key, r.label]));
  return {
    groups,
    roles,
    roleKeys: roles.map((r) => r.key),
    labelOf: (key) => labels[key] || titleise(key),
  };
};

export default useChurchRoles;
